/* eslint-disable css-modules/no-undef-class */
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { Swiper as SwiperInstance } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import Typography from '@/components/atoms/typography/typography';
import CarouselNavigation from '@/components/molecules/carousel-navigation/carousel-navigation';
import RecentSearchCard from '@/components/organisms/recent-search-card/recent-search-card';
import CarouselSlideContent from '@/components/utilities/carousel-slide-content/carousel-slide-content';
import Column from '@/components/utilities/column/column';
import Container from '@/components/utilities/container/container';
import ModuleWrapper, {
  ModuleWrapperProps,
} from '@/components/utilities/module-wrapper/module-wrapper';
import Row from '@/components/utilities/row/row';
import useQueryDepartures from '@/integrations/offers/departures/departures.hooks';
import { useI18n } from '@/libraries/contexts/i18n-context';
import {
  RecentSearch,
  useRecentSearchesContext,
} from '@/libraries/contexts/recent-searches-context/recent-searches-context';
import {
  CookieValues,
  useSearchContext,
} from '@/libraries/contexts/search-context';
import { updateUrlWithParameters } from '@/libraries/formatters/update-url-with-parameters';
import useDateFormatter from '@/libraries/formatters/use-date-formatter/use-date-formatter';
import breakpoints from '@/styles/modules/breakpoints.module.scss';
import styles from './recent-searches.module.scss';

export interface RecentSearchesProps extends ModuleWrapperProps {
  /** The amount of search cards to be shown */
  amountOfSearches: number;
  /** Headline for recent searches */
  headline: string;
  /** Search page url */
  searchPageUrl: string;
}

const extractCookieValues = (parameters: Record<string, any>): CookieValues => {
  const keys: (keyof CookieValues)[] = [
    'adultsNumber',
    'adultsPerRoom',
    'childrenAges',
    'childrenAgesPerRoom',
    'departureDate',
    'departureIds',
    'periodType',
  ];

  return Object.fromEntries(
    keys.map(key => [key, key in parameters ? parameters[key] : '']),
  ) as CookieValues;
};

const RecentSearches = ({
  amountOfSearches,
  headline,
  searchPageUrl,
  ...rest
}: RecentSearchesProps) => {
  const { t } = useI18n();
  const router = useRouter();
  const swiperRef = useRef<SwiperInstance>();
  const { dateFormatter } = useDateFormatter();

  const { updateCookies } = useSearchContext();
  const { recentSearches, trimRecentSearches } = useRecentSearchesContext();
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const { data: dataDepartures, isFetching: isFetchingDepartures } =
    useQueryDepartures({});

  useEffect(() => {
    trimRecentSearches(amountOfSearches);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getDuration = (durationType: string): string => {
    switch (durationType) {
      case 'week':
        return `${t('items.common.periodType.week')}`;

      case 'miniWeek':
        return `${t('items.common.periodType.miniWeek')}`;

      case 'twoWeeks':
        return `${t('items.common.periodType.twoWeeks')}`;

      case 'threeWeeks':
        return `${t('items.common.periodType.threeWeeks')}`;

      case 'custom':
        return `${t('items.common.periodType.custom')}`;

      default:
        return '';
    }
  };

  const getTotalAdultsNumber = (adultsText: string): number => {
    return adultsText
      ?.split('|')
      .map(Number)
      .reduce((sum, num) => sum + num, 0);
  };

  const getTotalChildrenNumber = (childrenText: string): number => {
    return childrenText
      ?.split('|')
      .flatMap(room => room.split(','))
      .filter(age => age.trim() !== '').length;
  };

  const getDurationText = (searchItem: RecentSearch): string | undefined => {
    if (!searchItem.parameters) return;

    const { durationType, departureDate } = searchItem.parameters;

    if (!durationType || !departureDate) return;

    return `${getDuration(durationType)} ${t('items.common.words.from')} ${dateFormatter(departureDate).format('D. MMM YYYY')}`;
  };

  const getPaxText = (searchItem: RecentSearch): string | undefined => {
    if (!searchItem.parameters) return;

    const { adultsPerRoom, adultsNumber, childrenAgesPerRoom, childrenAges } =
      searchItem.parameters;

    const adults = adultsPerRoom
      ? getTotalAdultsNumber(adultsPerRoom)
      : getTotalAdultsNumber(adultsNumber);
    const children = childrenAgesPerRoom
      ? getTotalChildrenNumber(childrenAgesPerRoom)
      : getTotalChildrenNumber(childrenAges);

    if (!adults) return;

    const adultText =
      adults === 1
        ? t('items.common.words.adult')
        : t('items.common.words.adults');
    const childText =
      children === 1
        ? t('items.common.words.child')
        : t('items.common.words.children');

    if (children) {
      return `${adults} ${adultText}, ${children} ${childText}`;
    } else {
      return `${adults} ${adultText}`;
    }
  };

  const getHeadlineText = (
    searchItem: RecentSearch,
  ): string[] | string | undefined => {
    if (!searchItem.locationSection) return;

    return searchItem.locationSection.value;
  };

  const getTransportMode = (searchItem: RecentSearch): string | undefined => {
    if (!dataDepartures) return;

    if (!searchItem.parameters) return;

    const departureId = searchItem.parameters.departureIds;
    const departureName =
      dataDepartures.result?.find(
        departure => departure.departureId.toString() === departureId,
      )?.departureName || '';

    return departureName;
  };

  const handleRecentSearchCardClick = (index: number) => {
    const recentSearchParams = recentSearches[index].parameters;

    if (!recentSearchParams) return;

    const parametersObject: Record<string, any> = {};

    new URLSearchParams(recentSearchParams).forEach((value, key) => {
      parametersObject[key] = value;
    });

    updateCookies(extractCookieValues(parametersObject));

    updateUrlWithParameters({
      newParameters: parametersObject,
      overwrite: true,
      redirectUrl: searchPageUrl,
      router,
      shouldNavigate: true,
    });
  };

  if (!isMounted) {
    return null;
  }

  if (recentSearches?.length === 0) {
    return null;
  }

  return (
    <ModuleWrapper {...rest} className={styles.component}>
      <Container>
        <Row className={styles.rowWrapper}>
          <Column>
            <Typography className={styles.headline} tagStyle="headingMedium">
              {headline}
            </Typography>
          </Column>

          <Column className={styles.itemsColumn}>
            <Swiper
              breakpoints={{
                [parseInt(breakpoints.mobile)]: {
                  spaceBetween: 16,
                  speed: 500,
                },
                [parseInt(breakpoints.tablet)]: {
                  spaceBetween: 32,
                  speed: 500,
                },
                [parseInt(breakpoints.laptop)]: {
                  spaceBetween: 32,
                  speed: 750,
                },
                [parseInt(breakpoints.desktop)]: {
                  spaceBetween: 32,
                  speed: 750,
                },
              }}
              className={styles.swiper}
              onSwiper={swiper => {
                swiperRef.current = swiper;
              }}
              slidesPerView="auto"
              wrapperClass={styles.swiperWrapper}
            >
              {recentSearches.map((searchItem, searchItemIndex) => {
                const headline = getHeadlineText(searchItem);
                const durationText = getDurationText(searchItem);
                const paxText = getPaxText(searchItem);
                const transportMode = getTransportMode(searchItem);

                return (
                  <SwiperSlide key={searchItemIndex} className={styles.slide}>
                    <CarouselSlideContent hideWhenOutOfView={false}>
                      <RecentSearchCard
                        key={searchItemIndex}
                        className={styles.item}
                        durationText={durationText}
                        headline={headline}
                        isLoading={isFetchingDepartures}
                        onClick={() =>
                          handleRecentSearchCardClick(searchItemIndex)
                        }
                        paxText={paxText}
                        transportMode={transportMode}
                      />
                    </CarouselSlideContent>
                  </SwiperSlide>
                );
              })}

              <CarouselNavigation
                ariaLabel={headline || undefined}
                isPointerOnly
                swiperRef={swiperRef}
              />
            </Swiper>
          </Column>
        </Row>
      </Container>
    </ModuleWrapper>
  );
};

RecentSearches.displayName = 'RecentSearches';

export default RecentSearches;
